import { SimulatorUI } from '@fruitful/money-map-simulator';
import type {
    MoneyMapAccountWithAccountDetails,
    MoneyMapWithRulesAndAccounts,
} from '@fruitful/types';
import React, { useMemo, useState } from 'react';
import { ScrollView } from 'react-native';
import { Text, View, XStack, YStack } from 'smoothie';

import { Button } from '../../common/components/GroveButtons';
import { APP_RIGHT_EDGE } from '../../common/utils/layout';
import { IS_PRODUCTION } from '../../config/FruitfulEnvironment';
import { transformToSimulation } from '../utils/transformToSimulation';

interface MoneyMapSimulatorButtonProps {
    moneyMap: MoneyMapWithRulesAndAccounts;
    accounts: MoneyMapAccountWithAccountDetails[];
}

export const MoneyMapSimulatorButton = ({
    moneyMap,
    accounts,
}: MoneyMapSimulatorButtonProps) => {
    const [isOpen, setIsOpen] = useState(false);

    const simulationJson = useMemo(() => {
        const sim = transformToSimulation(moneyMap, accounts);
        return JSON.stringify(sim, null, 2);
    }, [moneyMap, accounts]);

    if (IS_PRODUCTION) {
        return null;
    }

    return (
        <>
            <Button
                variant="secondary"
                size="tiny"
                onPress={() => {
                    setIsOpen(true);
                }}
            >
                Simulate (beta)
            </Button>
            {isOpen && (
                <SimulatorModal
                    initialJson={simulationJson}
                    onClose={() => {
                        setIsOpen(false);
                    }}
                />
            )}
        </>
    );
};

interface SimulatorModalProps {
    initialJson: string;
    onClose: () => void;
}

const SimulatorModal = ({ initialJson, onClose }: SimulatorModalProps) => {
    return (
        <View
            position="fixed"
            top={0}
            left={0}
            right={APP_RIGHT_EDGE}
            bottom={0}
            backgroundColor="rgba(0,0,0,0.5)"
            zIndex={9999}
            justifyContent="center"
            alignItems="center"
            onPress={onClose}
        >
            <View
                backgroundColor="$white"
                borderRadius={16}
                width="90%"
                maxWidth={1400}
                height="90vh"
                onPress={(e: { stopPropagation: () => void }) => {
                    e.stopPropagation();
                }}
                overflow="hidden"
            >
                <XStack
                    padding={16}
                    borderBottomWidth={1}
                    borderColor="#e5e7eb"
                    justifyContent="space-between"
                    alignItems="center"
                >
                    <Text fontSize={20} fontWeight="800">
                        Money Map Simulator (beta)
                    </Text>
                    <View
                        onPress={onClose}
                        cursor="pointer"
                        padding={8}
                        borderRadius={6}
                        hoverStyle={{ backgroundColor: '#f3f4f6' }}
                    >
                        <Text fontSize={18}>✕</Text>
                    </View>
                </XStack>

                <ScrollView style={{ flex: 1 }}>
                    <YStack padding={16}>
                        <SimulatorUI initialJson={initialJson} />
                    </YStack>
                </ScrollView>
            </View>
        </View>
    );
};
